import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";
import { object, string } from "yup";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import Input from "components/Input";
import { routes } from "constants/routes";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useAnalytics } from "hooks/useAnalytics";
import { IFactoringPaymentProfile, IPaymentProfile } from "models/HaulPay";
import { getMatchedEmailCarriersAPI, sendCarrierInvitationAPI } from "services/api/haulpay";
import styles from "./style.module.scss";

interface ICarrierGroup {
  id: string;
  name: string;
  type: string;
}

interface IInviteByEmailForm {
  email: string;
}

type InviteByEmailAddressProps = {
  paymentProfilesAll: IFactoringPaymentProfile[];
  carrierGroups: ICarrierGroup[];
};

export default function InviteByEmailAddress({
  paymentProfilesAll,
  carrierGroups
}: Readonly<InviteByEmailAddressProps>) {
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<IInviteByEmailForm>({
    resolver: yupResolver(
      object({
        email: string().required("This field is required")
      })
    )
  });

  const navigate = useNavigate();
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [isDuplicatedICModalOpen, toggleDuplicatedICModalOpen] = useToggle();
  const [matchedPaymentProfiles, setMatchedPaymentProfiles] = useState<IPaymentProfile[]>([]);

  const { trackAllEvents } = useAnalytics();

  const sendInvitation = async (email: string) => {
    try {
      setIsSendingInvite(true);
      await sendCarrierInvitationAPI(carrierGroups[0].id, email);
      trackAllEvents("broker_carrier_invite_sent");
      showSuccessMessage("Invite successfully sent.");
      setValue("email", "");
    } catch (e: any) {
      const errorMessage =
        e?.response?.data?.emails ||
        e?.response?.data?.detail ||
        "Error sending invitation to the email.";
      showErrorMessage(errorMessage);
    } finally {
      setIsSendingInvite(false);
    }
  };

  const proceedDuplicateEmail = () => {
    toggleDuplicatedICModalOpen();
    const allProfileIds = paymentProfilesAll.map((profile) => profile.payment_profile_id);
    navigate(routes.HAULPAY_BROKER_CARRIERs_NEW_FLOW, {
      state: {
        type: "existing_profile",
        profiles: matchedPaymentProfiles.map((profile) => ({
          ...profile,
          is_existing: allProfileIds.includes(profile.id)
        }))
      }
    });
  };

  const handleInviteCarrier = async (data: IInviteByEmailForm) => {
    const { email } = data;
    setIsSendingInvite(true);
    const { data: matchedResult } = await getMatchedEmailCarriersAPI({ matched_email: email });
    setMatchedPaymentProfiles(matchedResult.results);
    if (matchedResult.results.length) {
      toggleDuplicatedICModalOpen();
      setIsSendingInvite(false);
    } else {
      sendInvitation(email);
    }
  };

  return (
    <>
      <div className={styles.addCarrierWrapper}>
        <h3 className="text-xl font-medium">Add a Carrier</h3>
        <span className="text-tertiary">Add a single carrier using the form below</span>
      </div>

      <form className={styles.inviteForm} onSubmit={handleSubmit(handleInviteCarrier)}>
        <h3 className="text-lg font-medium">Invite via Email Address</h3>
        <div className={styles.formContent}>
          <Input
            type="email"
            name="email"
            register={register}
            error={errors.email}
            placeholder="Enter email"
            className="flex-1"
            inputClass="!border-foreground h-12"
          />
          <Button
            type="submit"
            variant="outline"
            disabled={isSendingInvite || isSubmitting}
            rounded
            className={styles.submitButton}>
            {isSendingInvite || isSubmitting ? (
              <Icon path={mdiLoading} size={1.5} spin={1} />
            ) : (
              "Invite"
            )}
          </Button>
        </div>
      </form>

      <ConfirmationModal
        content={
          <div className={styles.duplicateModalContent}>
            <span>Are you sure you want to invite this carrier?</span>
            <span>{"An existing profile matches the email address you've entered."}</span>
          </div>
        }
        isConfirmModalOpen={isDuplicatedICModalOpen}
        toggleConfirmModal={toggleDuplicatedICModalOpen}
        labelOKButton="Continue"
        onOK={proceedDuplicateEmail}
      />
    </>
  );
}
